import * as React from "react";
import cn from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./styles.module.scss";

interface IDisclosureProps {
    open?: boolean;
    children?: React.ReactNode;
    onChange?: () => void;
}

interface IDisclosureButtonProps {
    children?: React.ReactNode;
    className?: string;
}

interface IDisclosurePanelProps {
    children?: React.ReactNode;
    className?: string;
}

interface IDisclosureContext {
    open: boolean;
    onSelect: () => void;
    panelId: string;
}

const DisclosureContext = React.createContext<IDisclosureContext>({
    open: false,
    panelId: "",
    onSelect: () => {},
});

let id = 0;
const genId = () => ++id;

export function Disclosure({
    children,
    open: openProp,
    onChange,
}: IDisclosureProps) {
    const [open, setOpen] = React.useState(openProp ?? false);
    const { current: panelId } = React.useRef(String(genId()));

    const onSelect = React.useCallback(() => {
        onChange?.();
        setOpen((open) => !open);
    }, [onChange]);

    const context = React.useMemo(
        () => ({
            open,
            onSelect,
            panelId,
        }),
        [open, onSelect, panelId],
    );

    return (
        <DisclosureContext.Provider value={context}>
            {children}
        </DisclosureContext.Provider>
    );
}

export function DisclosureButton({
    children,
    ...restProps
}: IDisclosureButtonProps) {
    const { open, onSelect, panelId } = React.useContext<IDisclosureContext>(
        DisclosureContext,
    );

    function onClick(e: React.MouseEvent) {
        e.preventDefault();
        onSelect();
    }

    return (
        <button
            type="button"
            aria-controls={panelId}
            aria-expanded={open}
            onClick={onClick}
            {...restProps}
        >
            {children}
        </button>
    );
}

export function DisclosurePanel({
    children,
    className,
    ...restProps
}: IDisclosurePanelProps) {
    const { panelId, open } = React.useContext(DisclosureContext);

    return (
        <AnimatePresence initial={false}>
            {open && (
                <motion.div
                    id={panelId}
                    key={panelId}
                    initial="hidden"
                    animate="open"
                    exit="hidden"
                    variants={{
                        open: {
                            opacity: 1,
                            height: "auto",
                        },
                        hidden: {
                            opacity: 0,
                            height: 0,
                        },
                    }}
                    transition={{
                        duration: 0.15,
                    }}
                    {...restProps}
                    className={cn(styles.panel, className)}
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export function useDisclosureContext() {
    const { open, panelId } = React.useContext(DisclosureContext);

    return React.useMemo(
        () => ({
            open,
            panelId,
        }),
        [open, panelId],
    );
}
